import { useEffect, useMemo, useState } from 'react'
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Check,
  Loader2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  useCampaignDispositions,
  useCreateProspectNote,
  useProspectDetail,
} from '@/api/powerDialer'
import { styleForCategory } from '@/lib/dispositionCategory'
import type {
  Disposition,
  PowerDialerCampaign,
  PowerDialerProspect,
} from '@/types/powerDialer'

interface ColorPalette {
  dark: { bg: string; text: string; border: string }
  light: { bg: string; text: string; border: string }
}

// Palettes Tailwind par nom de couleur. Limite l'éventail pour cohérence
// visuelle (pas de couleurs random). Inconnue → slate.
const COLOR_PALETTES: Record<string, ColorPalette> = {
  green: {
    dark: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30' },
    light: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  },
  red: {
    dark: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
    light: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  },
  amber: {
    dark: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
    light: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  },
  purple: {
    dark: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
    light: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  },
  blue: {
    dark: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
    light: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  },
  slate: {
    dark: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30' },
    light: { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200' },
  },
}

function paletteFor(color: string): ColorPalette {
  return COLOR_PALETTES[color] || COLOR_PALETTES.slate
}

function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

function formatTime(dateStr: string, locale: string): string {
  const d = new Date(dateStr)
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
}

function renderScript(script: string, prospect: PowerDialerProspect): string {
  return script
    .replace(
      /\[nom\]/gi,
      prospect.full_name ||
        `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim(),
    )
    .replace(/\[prenom\]/gi, prospect.first_name || '')
    .replace(/\[entreprise\]/gi, prospect.company_name || '---')
    .replace(/\[poste\]/gi, prospect.position || '---')
    .replace(/\[telephone\]/gi, formatPhone(prospect.phone))
}

// Format ISO local pour <input type="datetime-local">. Le picker bouffe
// "YYYY-MM-DDTHH:MM" sans timezone — on construit en local pour éviter le
// décalage UTC sur les rappels du jour-même.
function toDateTimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function defaultCallbackValue(): string {
  // +1h par défaut, arrondi à la prochaine 5-minute
  const d = new Date(Date.now() + 60 * 60 * 1000)
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0)
  return toDateTimeLocal(d)
}

interface Props {
  campaignId: number
  campaign: PowerDialerCampaign
  activeProspect: PowerDialerProspect | null
  selectedDispositionSlug: string | null
  onSelectDisposition: (slug: string | null) => void
  callbackDate: string | null
  onCallbackDateChange: (iso: string | null) => void
  onConfirm: () => void
  saving: boolean
  darkMode: boolean
}

export function PdScriptPanel({
  campaignId,
  campaign,
  activeProspect,
  selectedDispositionSlug,
  onSelectDisposition,
  callbackDate,
  onCallbackDateChange,
  onConfirm,
  saving,
  darkMode,
}: Props) {
  const { t, i18n } = useTranslation()
  const dateLocale = i18n.language?.startsWith('en') ? 'en-CA' : 'fr-CA'
  const [noteText, setNoteText] = useState('')
  const [showSavedNotes, setShowSavedNotes] = useState(false)

  const dispositionsQuery = useCampaignDispositions(campaignId)
  const dispositions = useMemo<Disposition[]>(
    () => dispositionsQuery.data?.dispositions ?? [],
    [dispositionsQuery.data],
  )
  const selectedDisposition = useMemo(
    () => dispositions.find((d) => d.slug === selectedDispositionSlug) || null,
    [dispositions, selectedDispositionSlug],
  )
  const isCallback = selectedDisposition?.category === 'callback'

  const detailQuery = useProspectDetail(
    campaignId,
    activeProspect ? activeProspect.id : null,
  )
  const savedNotes = detailQuery.data?.notes || []
  const createNote = useCreateProspectNote(
    campaignId,
    activeProspect ? activeProspect.id : 0,
  )

  // Reset state à chaque changement de prospect
  useEffect(() => {
    setNoteText('')
    setShowSavedNotes(false)
  }, [activeProspect?.id])

  // Quand on choisit une disposition callback et qu'aucune date n'est
  // proposée, on suggère +1h pour faciliter le clic.
  useEffect(() => {
    if (isCallback && !callbackDate) {
      onCallbackDateChange(defaultCallbackValue())
    }
    if (!isCallback && callbackDate) {
      onCallbackDateChange(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCallback])

  const border = darkMode ? 'border-slate-800' : 'border-slate-200'
  const cardBg = darkMode ? 'bg-slate-900' : 'bg-white'
  const cardBgSubtle = darkMode ? 'bg-slate-900/50' : 'bg-slate-50'
  const inputBg = darkMode ? 'bg-slate-800' : 'bg-white'
  const textPrimary = darkMode ? 'text-white' : 'text-slate-900'
  const textSecondary = darkMode ? 'text-slate-300' : 'text-slate-600'
  const textMuted = darkMode ? 'text-slate-500' : 'text-slate-400'
  const textFaint = darkMode ? 'text-slate-700' : 'text-slate-300'
  const bg = darkMode ? 'bg-slate-950' : 'bg-slate-50'

  const handleSaveNote = async () => {
    if (!activeProspect || !noteText.trim() || createNote.isPending) return
    try {
      await createNote.mutateAsync(noteText.trim())
      setNoteText('')
    } catch {
      // toast géré ailleurs
    }
  }

  const canConfirm =
    !!selectedDispositionSlug && (!isCallback || !!callbackDate)

  return (
    <div
      className={cn(
        'w-[320px] border-l flex flex-col shrink-0',
        border,
        bg,
      )}
    >
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {/* Script */}
        <div className={cn('border rounded-lg p-4', cardBg, border)}>
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-[#F5A623]" />
            <h3
              className={cn(
                'text-xs font-medium uppercase tracking-widest',
                textMuted,
              )}
            >
              {t('powerDialer.session.script', 'Script')}
            </h3>
          </div>
          <div
            className={cn(
              'text-sm leading-relaxed whitespace-pre-wrap',
              textSecondary,
            )}
          >
            {activeProspect && campaign.script ? (
              renderScript(campaign.script, activeProspect)
            ) : campaign.script ? (
              campaign.script
            ) : (
              <span className={cn('italic', textFaint)}>
                {t('powerDialer.session.noScript', 'Aucun script défini pour cette campagne')}
              </span>
            )}
          </div>
        </div>

        {/* Dispositions */}
        <div className={cn('border rounded-lg p-4', cardBg, border)}>
          <h3
            className={cn(
              'text-xs font-medium uppercase tracking-widest mb-3',
              textMuted,
            )}
          >
            {t('powerDialer.session.disposition', 'Disposition')}
          </h3>
          {dispositionsQuery.isLoading ? (
            <div className="flex justify-center py-3">
              <Loader2 className={cn('w-4 h-4 animate-spin', textMuted)} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {dispositions.map((d) => {
                const isSelected = selectedDispositionSlug === d.slug
                // Couleur dérivée de la CATÉGORIE (source unique). La pastille
                // porte la sémantique ; le bouton reste neutre au repos.
                const catStyle = styleForCategory(d.category)
                const palette = paletteFor(catStyle.color)
                const swatch = darkMode ? palette.dark : palette.light
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() =>
                      onSelectDisposition(isSelected ? null : d.slug)
                    }
                    className={cn(
                      'flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all text-left',
                      isSelected
                        ? cn(swatch.bg, swatch.border, swatch.text, 'ring-1 ring-current')
                        : darkMode
                          ? 'border-slate-700 hover:bg-slate-800/60 text-slate-300'
                          : 'border-slate-200 hover:bg-slate-100 text-slate-600',
                    )}
                  >
                    <span
                      className={cn(
                        'w-2 h-2 rounded-full shrink-0',
                        catStyle.dot,
                      )}
                    />
                    <span className="text-xs font-semibold leading-tight truncate">
                      {d.name}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Datetime picker — apparaît uniquement quand on a choisi un callback */}
          {isCallback && (
            <div className="mt-3 space-y-1">
              <label
                className={cn('text-[10px] uppercase tracking-widest', textMuted)}
              >
                {t('powerDialer.session.callbackOn', 'Rappeler le')}
              </label>
              <input
                type="datetime-local"
                value={callbackDate || ''}
                min={toDateTimeLocal(new Date())}
                onChange={(e) => onCallbackDateChange(e.target.value || null)}
                className={cn(
                  'w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-amber-500',
                  inputBg,
                  border,
                  textPrimary,
                )}
              />
            </div>
          )}
        </div>

        {/* Note rapide */}
        <div className={cn('border rounded-lg p-4', cardBg, border)}>
          <div className="flex items-center justify-between mb-3">
            <h3
              className={cn(
                'text-xs font-medium uppercase tracking-widest',
                textMuted,
              )}
            >
              {t('powerDialer.session.quickNote', 'Note rapide')}
            </h3>
            {noteText.trim() && (
              <button
                type="button"
                onClick={() => void handleSaveNote()}
                disabled={createNote.isPending}
                className={cn(
                  'text-xs font-medium px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50',
                  darkMode
                    ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20'
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100',
                )}
              >
                {createNote.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  t('powerDialer.session.save', 'Sauvegarder')
                )}
              </button>
            )}
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={t('powerDialer.session.notePlaceholder', 'Note pour ce prospect...')}
            rows={3}
            disabled={!activeProspect}
            className={cn(
              'w-full border rounded-lg p-3 text-sm resize-none outline-none focus:ring-1 focus:ring-[#F5A623]',
              inputBg,
              border,
              textPrimary,
              darkMode ? 'placeholder-slate-600' : 'placeholder-slate-400',
              !activeProspect && 'opacity-50 cursor-not-allowed',
            )}
          />
          {savedNotes.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowSavedNotes((v) => !v)}
                className={cn(
                  'flex items-center gap-1 text-[10px] font-medium transition-colors',
                  textMuted,
                  darkMode ? 'hover:text-slate-300' : 'hover:text-slate-600',
                )}
              >
                {showSavedNotes ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
                {savedNotes.length}{' '}
                {savedNotes.length > 1
                  ? t('powerDialer.session.savedNotes', 'notes enregistrées')
                  : t('powerDialer.session.savedNote', 'note enregistrée')}
              </button>
              {showSavedNotes && (
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {savedNotes.map((note) => (
                    <div
                      key={note.id}
                      className={cn(
                        'text-xs p-2 rounded-lg',
                        darkMode ? 'bg-slate-800/50' : 'bg-slate-50',
                      )}
                    >
                      <p className={textSecondary}>{note.content}</p>
                      <p className={cn('text-[10px] mt-1', textMuted)}>
                        {formatTime(note.created_at, dateLocale)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bouton confirmer */}
        {selectedDispositionSlug && (
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving || !canConfirm}
            className="w-full bg-[#F5A623] hover:bg-[#e0951f] disabled:opacity-50 text-[#1A2233] py-3 rounded-lg font-bold text-sm transition-all active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg shadow-[#F5A623]/20"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {saving ? t('powerDialer.session.saving', 'Enregistrement...') : t('powerDialer.session.confirmNext', 'Confirmer & Suivant')}
          </button>
        )}
      </div>
      {/* Référence pour silencer le linter (cardBgSubtle utilisé dans la bottom bar plus tard) */}
      <span className="hidden" data-bg={cardBgSubtle} />
    </div>
  )
}
